import React from "react";
import { CreateFunction, UpdateFunction } from "@hex-insights/app-modules";
import {
	Button,
	ButtonVariant,
	ChildrenProps,
	ClassNameProps,
	Column,
	Conditional,
	DistributiveOmit,
	Else,
	getObjectValues,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	preventDefault,
	removeArrayElement,
	replaceArrayElement,
	RequiredKeys,
	Row,
	Rule,
	StyleProps,
	useEventListener,
} from "@hex-insights/core";
import {
	EditMode,
	Form,
	FormState,
	FormSubmitFunction,
	submissionSuccess,
	SubmitButton,
	useFormState,
	useInternalField,
	useOnSuccess,
} from "@hex-insights/forms";
import { Icon } from "../../../Components/Icon";
import { isTouchScreen, Message, MessageCreateMutation, MessageGroup, MessageUpdateMutation } from "../../../Utilities";
import * as MediaAttachmentComponents from "../../Components/MediaAttachment";
import { MediaAttachmentFormValues, MessageFormState, MessageFormValues } from "../../FormUtilities";
import * as MediaAttachmentUtilities from "../../Utilities/MediaAttachment";
import { MediaAttachmentCustomForm } from "../MediaAttachment";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import { useCreateFormControl, useCreateFormSubmission, useDetailFormControl, useDetailFormSubmission } from "./hooks";
import styles from "./styles.module.css";

export type ControlledCreateOnMessageGroupProps = Omit<
	CreateOnMessageGroupProps<MessageCreateMutation["createMessage"]>,
	"formState" | "create"
> & {
	formStateArgs?: MessageFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the Message model using an internally managed form state.
 */
export function ControlledCreateOnMessageGroup({ formStateArgs, ...props }: ControlledCreateOnMessageGroupProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <CreateOnMessageGroup formState={formState} create={create} {...props} />;
}

export type CreateOnMessageGroupProps<T> = {
	formState: FormState<MessageFormValues.Create>;
	messageGroupID: MessageGroup["id"];
	create: CreateFunction<MessageFormValues.Create, T>;
	onSuccess: (formState: FormState<MessageFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
	dragStartElementRef: React.MutableRefObject<HTMLDivElement | null>;
	onAddMediaAttachment?: () => void;
	bodyAreaClassName?: string;
	fileDropAreaClassName?: string;
} & BaseFormProps<MessageFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the Message model using the given form state.
 */
export function CreateOnMessageGroup<T>({
	formState,
	messageGroupID,
	create,
	onSuccess,
	formNameSuffix,
	dragStartElementRef,
	onAddMediaAttachment: onAddMediaAttachmentProp,
	bodyAreaClassName,
	fileDropAreaClassName,
	className,
	...props
}: CreateOnMessageGroupProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "message.create" + (formNameSuffix ? "." + formNameSuffix : "");

	useInternalField(formState, "messageGroupID", messageGroupID);
	useInternalField(formState, "messageTopic");

	const {
		onAddMediaAttachment: baseOnAddMediaAttachment,
		onUpdateMediaAttachment,
		onRemoveMediaAttachment,
	} = useMediaAttachmentsState(formState);
	const onAddMediaAttachment = React.useCallback(
		(formValues: MediaAttachmentFormValues.CreateInMessage) => {
			baseOnAddMediaAttachment(formValues);
			onAddMediaAttachmentProp?.();
		},
		[baseOnAddMediaAttachment, onAddMediaAttachmentProp],
	);

	return (
		<React.Fragment>
			<MediaAttachmentDropArea
				formState={formState}
				dragStartElementRef={dragStartElementRef}
				onAddMediaAttachment={onAddMediaAttachment}
				className={fileDropAreaClassName}
			/>

			<Form
				name={formName}
				formState={formState}
				onSubmit={formState.onSubmitWrapper(onSubmit)}
				{...props}
				className={className}
			>
				<Row justify="spaced-start" align="flex-end" className={bodyAreaClassName}>
					<Column style={{ flexGrow: 1 }}>
						<Field.Body formState={formState} noHeader autoFocus={!isTouchScreen()} style={{ width: "100%" }} />

						<If condition={formState.formValues.mediaAttachments.length > 0}>
							<MediaAttachmentsList
								mediaAttachmentFormValues={formState.formValues.mediaAttachments}
								onUpdateMediaAttachment={onUpdateMediaAttachment}
								onRemoveMediaAttachment={onRemoveMediaAttachment}
								style={{ padding: "0.5rem 0.5rem 0.25rem" }}
							/>
						</If>
					</Column>

					<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
						<SubmitButton
							variant="link"
							submissionStatus={formState.submissionStatus}
							onClick={formState.onSubmitWrapper(onSubmit)}
							disabled={
								(formState.formValues.body === "" && formState.formValues.mediaAttachments.length === 0) ||
								getObjectValues(formState.submissionLocks).some((e) => e)
							}
							className={styles["button--icon-circle"]}
						>
							<Icon.PaperPlaneTilt size={20} className={styles["button--icon-circle__icon"]} />
						</SubmitButton>

						<Rule thickness="2px" style={{ height: "1.5rem" }} />

						<MediaAttachmentAddButton
							onAdd={onAddMediaAttachment}
							numItems={formState.formValues.mediaAttachments.length}
							className={styles["button--icon-circle"]}
						>
							<Icon.Plus size={20} className={styles["button--icon-circle__icon"]} />
						</MediaAttachmentAddButton>
					</Row>
				</Row>
			</Form>
		</React.Fragment>
	);
}

export type MediaAttachmentDropAreaProps = {
	formState: FormState<Pick<MessageFormValues.Create, "mediaAttachments">>;
	dragStartElementRef: React.MutableRefObject<HTMLDivElement | null>;
	onAddMediaAttachment: (formValues: MediaAttachmentFormValues.CreateInMessage) => void;
} & Partial<ClassNameProps & StyleProps>;

export function MediaAttachmentDropArea({
	formState,
	dragStartElementRef,
	onAddMediaAttachment,
	className,
	style,
}: MediaAttachmentDropAreaProps) {
	const [isDragging, setIsDragging] = React.useState(false);
	const startDrag = React.useCallback(() => {
		setIsDragging(true);
	}, []);
	const onDragLeave = React.useCallback(() => {
		setIsDragging(false);
	}, []);
	useEventListener(dragStartElementRef.current, "dragenter", startDrag);

	const [uploadingCount, setUploadingCount] = React.useState(0);
	const uploadFiles = MediaAttachmentUtilities.useUploadFiles(
		formState,
		onAddMediaAttachment,
		MediaAttachmentFormValues.blankItemInMessage,
		uploadingCount,
		setUploadingCount,
	);
	const onDrop = React.useCallback<React.DragEventHandler>(
		(event) => {
			event.preventDefault();
			setIsDragging(false);
			uploadFiles(event.dataTransfer.files);
		},
		[uploadFiles],
	);

	return (
		<div
			onDragOver={preventDefault}
			onDragLeave={onDragLeave}
			onDrop={onDrop}
			className={makeClassName(
				styles["create-on-message-group-from__file-drop-area"],
				isDragging ? styles["create-on-message-group-from__file-drop-area--dragging"] : "",
				uploadingCount > 0 ? styles["create-on-message-group-from__file-drop-area--uploading"] : "",
				className,
			)}
			style={style}
		>
			<div className={styles["create-on-message-group-form__file-drop-area__content"]}>
				{uploadingCount > 0 ? "Uploading..." : "Drop files here to upload"}
			</div>
		</div>
	);
}

export function useMediaAttachmentsState(formState: FormState<Pick<MessageFormValues.Create, "mediaAttachments">>) {
	useInternalField(formState, "mediaAttachments");

	const {
		formSetFunctions: { mediaAttachments: setMediaAttachments },
	} = formState;

	const onAddMediaAttachment = React.useCallback(
		(formValues: MediaAttachmentFormValues.CreateInMessage) => {
			setMediaAttachments((prev) => [...prev, formValues]);
		},
		[setMediaAttachments],
	);
	const onUpdateMediaAttachment = React.useCallback(
		(index: number, formValues: MediaAttachmentFormValues.CreateInMessage) => {
			setMediaAttachments((prev) => replaceArrayElement(prev, index, formValues));
		},
		[setMediaAttachments],
	);
	const onRemoveMediaAttachment = React.useCallback(
		(index: number) => {
			setMediaAttachments((prev) => removeArrayElement(prev, index));
		},
		[setMediaAttachments],
	);

	return {
		onAddMediaAttachment,
		onUpdateMediaAttachment,
		onRemoveMediaAttachment,
	};
}

export type MediaAttachmentAddButtonProps = {
	variant?: ButtonVariant;
	numItems: number;
	onAdd: (formValues: MediaAttachmentFormValues.CreateInMessage) => void;
} & Partial<ClassNameProps & ChildrenProps>;

export function MediaAttachmentAddButton({
	numItems,
	onAdd,
	variant = "link",
	className,
	children,
}: MediaAttachmentAddButtonProps) {
	const initialFormValues = React.useMemo(
		() => ({
			...MediaAttachmentFormValues.blankItemInMessage,
			displayOrder: numItems,
			isFile: true,
		}),
		[numItems],
	);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const onSuccess = React.useCallback(
		(formState: FormState<MediaAttachmentFormValues.CreateInMessage>) => {
			onAdd(formState.formValues);
			closeModal();
		},
		[onAdd, closeModal],
	);

	return (
		<React.Fragment>
			<Button
				variant={variant}
				onClick={openModal}
				className={makeClassName(!children ? styles["button--icon-circle"] : "", className)}
			>
				{children ?? <Icon.Paperclip size={20} className={styles["button--icon-circle__icon"]} />}
			</Button>

			<Modal.If condition={isModalOpen}>
				<MediaAttachmentModal
					onClose={closeModal}
					initialFormValues={initialFormValues}
					onSuccess={onSuccess}
					onCancel={closeModal}
					isInitialOpenTime
				/>
			</Modal.If>
		</React.Fragment>
	);
}

export type MediaAttachmentsListProps = {
	mediaAttachmentFormValues: MediaAttachmentFormValues.CreateInMessage[];
	onUpdateMediaAttachment: (index: number, formValues: MediaAttachmentFormValues.CreateInMessage) => void;
	onRemoveMediaAttachment: (index: number) => void;
} & Partial<ClassNameProps & StyleProps>;

export function MediaAttachmentsList({
	mediaAttachmentFormValues,
	onUpdateMediaAttachment,
	onRemoveMediaAttachment,
	className,
	style,
}: MediaAttachmentsListProps) {
	return (
		<Row justify="spaced-start" withVerticalSpacing overflow="wrap" className={className} style={style}>
			{mediaAttachmentFormValues.map((formValues, i) => (
				<MediaAttachmentFormPreview
					key={i}
					formValues={formValues}
					index={i}
					onUpdate={onUpdateMediaAttachment}
					onRemove={onRemoveMediaAttachment}
				/>
			))}
		</Row>
	);
}

type MediaAttachmentFormPreviewProps = {
	formValues: MediaAttachmentFormValues.CreateInMessage;
	index: number;
	onUpdate: (index: number, formValues: MediaAttachmentFormValues.CreateInMessage) => void;
	onRemove: (index: number) => void;
};

function MediaAttachmentFormPreview({ formValues, index, onUpdate, onRemove }: MediaAttachmentFormPreviewProps) {
	const onRemoveClick = React.useCallback(() => {
		onRemove(index);
	}, [onRemove, index]);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const onSuccess = React.useCallback(
		(formState: FormState<MediaAttachmentFormValues.CreateInMessage>) => {
			onUpdate(index, formState.formValues);
			closeModal();
		},
		[onUpdate, index, closeModal],
	);

	return (
		<React.Fragment>
			<div className={styles["media-attachment-preview"]}>
				<div className={styles["media-attachment-preview__remove-button__container"]}>
					<Button variant="link" onClick={onRemoveClick} className={styles["media-attachment-preview__remove-button"]}>
						<Icon.X className={styles["media-attachment-preview__remove-button__icon"]} />
					</Button>
				</div>

				<div onClick={openModal} className={styles["media-attachment-preview__preview__container"]}>
					<Conditional>
						<If condition={formValues.isFile && MediaAttachmentUtilities.isVisual(formValues)}>
							<MediaAttachmentComponents.VisualAttachmentPreview
								formValues={formValues}
								className={styles["media-attachment-preview__preview"]}
							/>
						</If>
						<If condition={formValues.isFile}>
							<MediaAttachmentComponents.FileAttachmentPreview
								formValues={formValues}
								className={styles["media-attachment-preview__preview"]}
							/>
						</If>
						<Else>
							<MediaAttachmentComponents.LinkAttachmentPreview
								formValues={formValues}
								className={styles["media-attachment-preview__preview"]}
							/>
						</Else>
					</Conditional>
				</div>
			</div>

			<Modal.If condition={isModalOpen}>
				<MediaAttachmentModal
					onClose={closeModal}
					initialFormValues={formValues}
					onSuccess={onSuccess}
					onCancel={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

type MediaAttachmentModalProps = RequiredKeys<Pick<ModalProps, "ifRef" | "onClose">, "onClose"> & {
	initialFormValues: MediaAttachmentFormValues.CreateInMessage;
	isInitialOpenTime?: boolean;
	onCancel: () => void;
	onSuccess: (formState: FormState<MediaAttachmentFormValues.CreateInMessage>) => void;
};

function MediaAttachmentModal({
	ifRef,
	onClose,
	initialFormValues,
	onCancel,
	onSuccess,
	isInitialOpenTime = false,
}: MediaAttachmentModalProps) {
	const formState = useFormState({ initialFormValues, editMode: EditMode.WriteOnly });

	const onSubmit = React.useCallback<FormSubmitFunction<MediaAttachmentFormValues.CreateInMessage>>(async () => {
		return submissionSuccess();
	}, []);
	useOnSuccess(formState, onSuccess);

	return (
		<Modal size="small" ifRef={ifRef} onClose={onClose}>
			<Modal.Header>
				<Heading.H2 noMargin>Attachment</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Form formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} noNotifications>
					<Form.Column>
						<MediaAttachmentCustomForm.CreateFieldsInMessage formState={formState} />

						<Form.ActionBar>
							<If condition={isInitialOpenTime}>
								<Button variant="secondary" onClick={onCancel}>
									Cancel
								</Button>
							</If>
							<SubmitButton
								onClick={formState.onSubmitWrapper(onSubmit)}
								submissionStatus={formState.submissionStatus}
								disabled={getObjectValues(formState.submissionLocks).some((e) => e)}
							>
								{isInitialOpenTime ? "Add Attachment" : "Update Attachment"}
							</SubmitButton>
						</Form.ActionBar>
					</Form.Column>
				</Form>
			</Modal.Body>
		</Modal>
	);
}

export type EditFormValues = Pick<MessageFormValues.Detail, "body">;

const editFields = {
	body: true,
} as const;

export type ControlledEditProps = Omit<EditProps<MessageUpdateMutation["updateMessage"]>, "formState" | "update"> & {
	formStateArgs?: Partial<DistributiveOmit<MessageFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

/**
 * Edit form of the Message model using an internally managed form state.
 */
export function ControlledEdit({ formStateArgs, ...props }: ControlledEditProps) {
	const { formState, update } = useDetailFormControl<"body">({
		...formStateArgs,
		message: props.message,
		fields: editFields,
	});

	return <Edit formState={formState} update={update} {...props} />;
}

export type EditProps<T> = {
	message: Pick<Message, "id" | "body"> & {
		mediaAttachments: MediaAttachmentCustomForm.ControlledMultiEditFormProps["mediaAttachments"];
	};
	formState: FormState<EditFormValues>;
	update: UpdateFunction<EditFormValues, T>;
	onSuccess: (formState: FormState<EditFormValues>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<EditFormValues> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Edit form of the Message model using the given form state.
 */
export function Edit<T>({
	formState,
	message,
	update,
	onSuccess,
	formNameSuffix,
	noChangesDialog = true,
	...props
}: EditProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName = `message.detail.${message.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form
			name={formName}
			formState={formState}
			onSubmit={formState.onSubmitWrapper(onSubmit)}
			noChangesDialog={noChangesDialog}
			{...props}
		>
			<Form.Column style={{ width: "100%" }}>
				<Field.Body formState={formState} noHeader style={{ width: "100%" }} />
				<MediaAttachmentCustomForm.ControlledMultiEditForm
					messageID={message.id}
					mediaAttachments={message.mediaAttachments}
				/>
				<If condition={noChangesDialog}>
					<Form.ActionBar>
						<SubmitButton
							submissionStatus={formState.submissionStatus}
							onClick={formState.onSubmitWrapper(onSubmit)}
							disabled={formState.formValues.body === "" && message.mediaAttachments.length === 0}
						>
							Save Changes
						</SubmitButton>
					</Form.ActionBar>
				</If>
			</Form.Column>
		</Form>
	);
}
